import BaseWrapper from "../components/BaseWrapper/BaseWrapper";
import { useEffect, useState } from "react";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { API } from "../lib/api";
import dayjs from "dayjs";
import Feedback from "../components/Feedback/Feedback";
import { useAuth } from "../contexts/AuthContext";
import { getForecastMarginProductGroups, useAppSettings } from "../contexts/AppSettingsContext";

const stages = ['01 - Assign Resources', '02 - Submittals Sent', '03 - Submittals Approved', '04 - Shipped', '05 - Installed', '06 - Commissioned', '07 - Revenue Taken', '10 - Closed'];

const SingleRevenueLinePage = () => {
    const { lid } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const oid = searchParams.get("oid");
    const { user } = useAuth();
    const { applicationSettings } = useAppSettings();

    const [line, setLine] = useState({});
    const [feedback, setFeedback] = useState({ open: false, message: "", type: "" });
    const [formData, setFormData] = useState({
        productGroup: "",
        lineComments: "",
        bookedOrdersStage: "",
        amount: 0,
        shipDate: ""
    });

    const role = String(user?.role || localStorage.getItem("role") || "").trim().toLowerCase();
    const lineTypes = getForecastMarginProductGroups(applicationSettings);
    const canEditProductGroup = role === "admin" || role === "manager";
    const canEditBookedOrdersStage = role === "admin" || role === "manager";
    const canEditAmount = role === "admin";
    const canEditShipDate = true;

    const hydrateForm = (lineData) => setFormData({
        productGroup: lineData?.productGroup || "",
        lineComments: lineData?.lineComments || "",
        bookedOrdersStage: lineData?.bookedOrdersStage || "",
        amount: lineData?.amount || 0,
        shipDate: lineData?.shipDate || ""
    });

    const handleFieldChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

    const handleSubmit = async () => {
        const updatedFields = {};

        if (canEditProductGroup && formData.productGroup !== (line?.productGroup || "")) {
            updatedFields.productGroup = formData.productGroup;
        }

        if (canEditBookedOrdersStage && formData.bookedOrdersStage !== (line?.bookedOrdersStage || "")) {
            updatedFields.bookedOrdersStage = formData.bookedOrdersStage;
        }

        if (canEditAmount && Number(formData.amount) !== Number(line?.amount || 0)) {
            updatedFields.amount = Number(formData.amount) || 0;
        }

        const currentShipDate = formData.shipDate ? dayjs(formData.shipDate).toISOString() : "";
        const originalShipDate = line?.shipDate ? dayjs(line.shipDate).toISOString() : "";
        if (canEditShipDate && currentShipDate !== originalShipDate) {
            updatedFields.shipDate = formData.shipDate;
        }

        if (!Object.keys(updatedFields).length) {
            setFeedback({ open: true, message: "No allowed revenue line changes to save.", type: "info" });
            return;
        }

        const response = await API.Lines.UpdateSingle(lid, oid, updatedFields);

        if (response.status === 200) {
            const updatedLine = response.data?.data || line;
            setLine(updatedLine);
            hydrateForm(updatedLine);
            setFeedback({ open: true, message: response.data.message, type: "success" });
        } else {
            setFeedback({ open: true, message: response?.data?.message || `Failed to update Opportunity Line: ${line.name}`, type: "error" });
        }
    };

    const handleReturnToOpp = () => navigate(`/opportunities/${oid}`);

    useEffect(() => {
        async function getData() {
            const response = await API.Lines.FetchById(lid);
            setLine(response);
            hydrateForm(response);
        }

        getData();
    }, [lid]);

    return (
        <BaseWrapper content={
            <>
                <Feedback props={feedback} setFeedback={setFeedback} />
                <div className="col-lg-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="mb-0">Revenue Line - <span>{line?.lineComments || "No Description"}</span></h5>
                        </div>
                        <div className="card-body">
                            <h6 style={{ margin: "1em" }}>Update Revenue Line</h6>
                            <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-evenly" }}>
                                <div className='col-10 col-lg-5' style={{ marginBottom: "1em" }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="revenue-line-type-label">Type</InputLabel>
                                        <Select
                                            labelId="revenue-line-type-label"
                                            value={formData.productGroup}
                                            label="Type"
                                            onChange={(e) => handleFieldChange("productGroup", e.target.value)}
                                            disabled={!canEditProductGroup}
                                        >
                                            {lineTypes.map((type) => (
                                                <MenuItem key={type} value={type}>{type}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='col-10 col-lg-5' style={{ marginBottom: "1em" }}>
                                    <FormControl fullWidth>
                                        <TextField
                                            value={formData.lineComments}
                                            label="Line Description"
                                            disabled
                                            helperText="Revenue line descriptions are synchronized from the quote header."
                                        />
                                    </FormControl>
                                </div>

                                <div className='col-10 col-lg-5' style={{ marginBottom: "1em" }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="revenue-line-stage-label">Booked Orders Stage</InputLabel>
                                        <Select
                                            labelId="revenue-line-stage-label"
                                            value={formData.bookedOrdersStage}
                                            label="Booked Orders Stage"
                                            onChange={(e) => handleFieldChange("bookedOrdersStage", e.target.value)}
                                            disabled={!canEditBookedOrdersStage}
                                        >
                                            {stages.map((stage) => (
                                                <MenuItem key={stage} value={stage}>{stage}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className='col-10 col-lg-5' style={{ marginBottom: "1em" }}>
                                    <FormControl fullWidth>
                                        <TextField
                                            type="number"
                                            label="Amount"
                                            value={formData.amount}
                                            onChange={(e) => handleFieldChange("amount", e.target.value)}
                                            disabled={!canEditAmount}
                                        />
                                    </FormControl>
                                </div>

                                <div className='col-10 col-lg-5' style={{ margin: "0.5em" }}>
                                    <FormControl fullWidth>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DatePicker
                                                label="Ship Date"
                                                value={formData.shipDate ? dayjs(formData.shipDate) : null}
                                                onChange={(date) => handleFieldChange("shipDate", date)}
                                                format="MM/DD/YYYY"
                                                disabled={!canEditShipDate}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </div>
                            </div>

                            <div style={{ display: "flex", justifyContent: "center", marginBottom: "2em" }}>
                                <Button variant="outlined" onClick={handleSubmit}>Save Changes</Button>
                            </div>
                            <div style={{ display: "flex", justifyContent: "center", marginBottom: "2em" }}>
                                <Button variant="outlined" color="warning" onClick={handleReturnToOpp}>Return to Opportunity</Button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        } />
    );
};

export default SingleRevenueLinePage;
